/**
 * NativeLockScreenLike
 *
 * Native-only. Puts a heart on the lock-screen / Control Center / CarPlay
 * now-playing card (SoundCloud-style) and keeps it in sync with the user's
 * likes.
 *
 * Same shape as NativeLockScreenRefill: renders nothing, owns no state,
 * no-ops on web/Electron.
 *
 * TWO DIRECTIONS
 *   native → JS   'remoteCommand' {action:'like'} when the heart is tapped.
 *                 Native deliberately does NOT decide the outcome — it has no
 *                 idea whether the track is already liked. We do.
 *   JS → native   engine.setLikeState({liked, enabled}) whenever the playing
 *                 track or its like state changes.
 *
 * WHY `enabled` MATTERS
 * The heart is offered ONLY when we can identify what is playing well enough
 * to like it. A like carrying the wrong id is not a cosmetic bug: the thunk's
 * own comment says "a like without an id is merely un-hydrated; a like with a
 * WRONG id is poisoned". So when identification fails, we hide the heart
 * rather than guess.
 *
 * IDENTIFYING THE TRACK
 * The plugin reports the URL of the item it has loaded on every playerState,
 * which Redux keeps as `currentUrl`. That is the reliable key — it survives
 * auto-advance and refill, neither of which JS can resolve by play index
 * (audioList holds a single entry on native). We match it against the
 * listContext to recover the full track object, and hence its catalog id.
 * Falling back to title+artist would risk matching a different remix or one
 * of the placeholder-credit rows, which is exactly how a wrong id happens.
 */

import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getNativePlaybackEngine } from '../playback/NativePlaybackEngine';
import { isNative } from '../utils/webOnly';
import { likeTrack, unlikeTrack } from '../redux/thunk/likesThunk';
import { entityId } from '../utils/entityId';
import { sampleUrlOf } from '../utils/resolvePlaybackSrc';

// Same id-set memoization LikeButton uses, for the same reason: match by id
// first (robust), text key second (drifts between data sources).
let _likedRef = null;
let _likedIdSet = new Set();
const likedIdsOf = (likedTracks) => {
    if (likedTracks === _likedRef) return _likedIdSet;
    _likedRef = likedTracks;
    _likedIdSet = new Set((likedTracks || []).map((t) => entityId(t)).filter(Boolean));
    return _likedIdSet;
};

function NativeLockScreenLike() {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
    // Primitives, not the currentTrack object: setNativePlayerState rebuilds
    // that object on every 4Hz tick, and subscribing to it would re-render
    // this component (and re-run the sync effect) four times a second.
    const currentName = useSelector((s) => s.musicPlayer.currentTrack?.name || '');
    const currentSinger = useSelector((s) => s.musicPlayer.currentTrack?.singer || '');
    const currentUrl = useSelector((s) => s.musicPlayer.currentUrl || '');
    const likedSet = useSelector((s) => s.likes.likedSet);
    const likedTracks = useSelector((s) => s.likes.likedTracks);
    const listContextItems = useSelector((s) => s.listContext.items);

    // Resolve the playing track back to a full object so a like carries its
    // catalog id. Match on the plugin's own URL; null when we can't be sure.
    const resolved = (() => {
        if (!currentUrl || !Array.isArray(listContextItems)) return null;
        return listContextItems.find((t) => t && sampleUrlOf(t) === currentUrl) || null;
    })();

    const artist = resolved?.artist || currentSinger;
    const title = resolved?.title || currentName;
    const identifiable = !!(isAuthenticated && artist && title);

    const pid = resolved ? entityId(resolved) : null;
    const key = artist && title ? `${artist}|||${title}` : null;
    const isLiked = (pid && likedIdsOf(likedTracks).has(pid)) || (key ? !!likedSet[key] : false);

    // The tap handler is registered once; route the values it needs through
    // refs so a re-subscribe isn't needed on every like/track change.
    const stateRef = useRef({});
    stateRef.current = { resolved, artist, title, identifiable, isLiked };

    useEffect(() => {
        if (!isNative) return undefined;
        const engine = getNativePlaybackEngine();
        if (!engine.isAvailable()) return undefined;
        const off = engine.on('remoteCommand', ({ action }) => {
            if (action !== 'like') return;
            const { resolved: r, artist: a, title: t, identifiable: ok, isLiked: liked } = stateRef.current;
            // Should be unreachable — the heart is disabled when we can't
            // identify the track — but a stale lock-screen card can still
            // deliver a tap, so never act on a guess.
            if (!ok) return;
            if (liked) {
                dispatch(unlikeTrack({ artist: a, title: t, likeSource: 'lock_screen' }));
            } else {
                // Prefer the resolved listContext object: it carries the
                // catalog id, artwork and sample url, so the like lands as a
                // complete, playable row rather than a bare text key.
                dispatch(likeTrack({
                    ...(r || {}),
                    artist: a,
                    title: t,
                    likeSource: 'lock_screen',
                }));
            }
        });
        return () => { off(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Push state to the lock screen. Skipping redundant writes keeps this off
    // the 4Hz path entirely — the signature only moves on a real change.
    const lastSigRef = useRef('');
    useEffect(() => {
        if (!isNative) return;
        const engine = getNativePlaybackEngine();
        if (!engine.isAvailable()) return;
        const sig = `${identifiable ? 1 : 0}|${isLiked ? 1 : 0}`;
        if (sig === lastSigRef.current) return;
        lastSigRef.current = sig;
        engine.setLikeState({ liked: isLiked, enabled: identifiable });
    }, [identifiable, isLiked]);

    return null;
}

export default NativeLockScreenLike;
